import rectificationOptions from 'const/rectificationOptions'
import useCustomTranslation from 'hooks/useCustomTranslation'
import { StyledCheckbox, Subtext } from 'pages/InfoRequestPage.styles'
import { ComponentInterface, SFDDLProps } from 'typings'
import MultiSelect from 'ui/atoms/MultiSelect'
import { MultiSelectOption } from 'ui/atoms/MultiSelect/typings'
import { CheckboxContainerCenter } from '../RequestPage.styles'

// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace window {
  // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
  const _SFDDL: SFDDLProps
}

const setRequestTypeAnalytics = (value: string, checked: boolean) => {
  // eslint-disable-next-line no-undef, @typescript-eslint/naming-convention, no-underscore-dangle
  const { RequestType: requestType } = window._SFDDL.requestcheckboxInfo
  let values = requestType ? requestType.split('|') : []
  if (checked) {
    values.push(value)
  } else {
    values = values.filter(item => item !== value)
  }
  // eslint-disable-next-line no-undef, @typescript-eslint/naming-convention, no-underscore-dangle
  window._SFDDL.requestcheckboxInfo.RequestType = values.join('|')
}

const getRectificationAnalytics = (values: MultiSelectOption[]) => {
  const valuesMap = values.map(i => i.value)
  const filtered = rectificationOptions.filter(
    item => valuesMap.indexOf(item.value) > -1
  )
  return filtered.map(item => item.dataReason).join('|')
}

const CaliforniaOptions = ({
  formValues,
  handleState,
  handleOnBlur,
  handleOnChange
}: ComponentInterface): JSX.Element => {
  const t = useCustomTranslation('form')

  // eslint-disable-next-line sonarjs/no-duplicate-string
  const rightTo = t('request.rightTo')
  const isEmployee = formValues.employee?.value === 'Yes'

  return (
    <>
      <StyledCheckbox
        id="accessPersonalInfo"
        isChecked={formValues.accessPersonalInfo}
        onChange={() => {
          const checked = !formValues.accessPersonalInfo
          handleOnChange('accessPersonalInfo', checked)
          setRequestTypeAnalytics('AccessPI', checked)
        }}
        data-testid="access-personal-info"
        data-reason="righttoaccesspersonalinformation"
        data-type="Checkbox"
        className="ensightencustomevent"
      >
        {rightTo + t('request.access')}
      </StyledCheckbox>
      <StyledCheckbox
        id="erasurePersonalInfo"
        isChecked={formValues.erasurePersonalInfo}
        onChange={() => {
          const checked = !formValues.erasurePersonalInfo
          handleOnChange('erasurePersonalInfo', checked)
          setRequestTypeAnalytics('ErasurePI', checked)
        }}
        data-testid="erasure-personal-info"
        data-reason="righttoerasurepersonalinformation"
        data-type="Checkbox"
        className="ensightencustomevent"
      >
        {rightTo + t('request.erasure')}
      </StyledCheckbox>
      <Subtext className="subtext-up">{t('request.erasureSubtext')}</Subtext>
      <CheckboxContainerCenter style={{ marginBottom: '1rem' }}>
        <StyledCheckbox
          id="rightToRectification"
          isChecked={formValues.rightToRectification}
          onChange={() => {
            const rightToRectification = !formValues.rightToRectification
            // clean up if it's uncheck
            if (!rightToRectification) {
              // Clean up values for rectification
              // eslint-disable-next-line no-undef, @typescript-eslint/naming-convention, no-underscore-dangle
              window._SFDDL.requestcheckboxInfo.RectificationType = ''
            }
            handleState({
              ...formValues,
              rightToRectification,
              rectification: formValues.rightToRectification
                ? formValues.rectification
                : [],
              errors: {
                ...formValues.errors,
                rectification: formValues.rightToRectification
                  ? formValues.errors.rectification
                  : ''
              }
            })
            setRequestTypeAnalytics('Rectification', rightToRectification)
          }}
          data-testid="right-rectification"
          data-reason="righttorectification"
          data-type="Checkbox"
          className="ensightencustomevent rectification-checkbox"
        >
          {rightTo + t('request.rectification')}
        </StyledCheckbox>
        <MultiSelect
          name="rectification"
          placeholder={t('optionPlaceholder')}
          selectedOptions={formValues.rectification}
          options={rectificationOptions}
          error={formValues.errors.rectification}
          disabled={!formValues.rightToRectification}
          testId="select-rectification"
          onBlur={() =>
            formValues.rightToRectification &&
            !formValues.rectification.length &&
            handleOnBlur('rectification')
          }
          onChange={(values: MultiSelectOption[]) => {
            handleOnChange('rectification', values)
            // eslint-disable-next-line no-undef, @typescript-eslint/naming-convention, no-underscore-dangle
            window._SFDDL.requestcheckboxInfo.RectificationType =
              values?.length === rectificationOptions.length
                ? 'all'
                : getRectificationAnalytics(values)
          }}
        />
      </CheckboxContainerCenter>
      <Subtext className="subtext-up">
        {t('request.rectificationSubtext')}
      </Subtext>
      <StyledCheckbox
        id="rightToOptOutOfSharing"
        isChecked={formValues.rightToOptOutOfSharing}
        isDisabled={isEmployee}
        onChange={() => {
          const checked = !formValues.rightToOptOutOfSharing
          handleOnChange('rightToOptOutOfSharing', checked)
          setRequestTypeAnalytics('Optoutofsharing', checked)
        }}
        data-testid="right-sharing"
        data-reason="righttooptoutofsharing"
        data-type="Checkbox"
        className="ensightencustomevent"
      >
        {rightTo + t('request.sharing')}
      </StyledCheckbox>
      <CheckboxContainerCenter>
        <StyledCheckbox
          id="rightToOptOutOfUseOfSensitivePII"
          isChecked={formValues.rightToOptOutOfUseOfSensitivePII}
          onChange={() => {
            const checked = !formValues.rightToOptOutOfUseOfSensitivePII
            handleOnChange('rightToOptOutOfUseOfSensitivePII', checked)
            setRequestTypeAnalytics('OptoutofPII', checked)
          }}
          data-testid="right-sensitive-pii"
          data-reason="righttooptoutofuseofsensitivepii"
          data-type="Checkbox"
          className="ensightencustomevent"
        >
          {rightTo + t('request.sensitive')}
        </StyledCheckbox>
      </CheckboxContainerCenter>
      <StyledCheckbox
        id="rightToTransparencyForProfiling"
        isDisabled={isEmployee}
        isChecked={formValues.rightToTransparencyForProfiling}
        onChange={() => {
          const checked = !formValues.rightToTransparencyForProfiling
          handleOnChange('rightToTransparencyForProfiling', checked)
          setRequestTypeAnalytics('Transparencyprofiling', checked)
        }}
        data-testid="right-transparency-profiling"
        data-reason="righttotransparencyforprofiling"
        data-type="Checkbox"
        className="ensightencustomevent"
      >
        {rightTo + t('request.transparency')}
      </StyledCheckbox>
    </>
  )
}

export default CaliforniaOptions
